/*
 * Drives the mobile product-grid swipe carousel (sections/product-grid.
 * liquid, snippets/aleo-product-tile.liquid): a plain NATIVE horizontal
 * scroll-snap track (.aleo-tile__mobile-track), not a custom pointer/
 * transform-driven one.
 *
 * Why native, again: an earlier version of this carousel used native
 * scroll-snap with a single clone slide before the first image and after
 * the last, silently resetting scrollLeft onto the real counterpart once
 * a gesture settled on a clone -- that reset visibly skipped during iOS
 * Safari's momentum scrolling. The fix tried next was a fully custom
 * pointer-driven, transform-based track, so JS controlled the position at
 * every instant instead of fighting native momentum -- that solved the
 * skip at the loop point, but introduced its own iOS Safari-specific
 * glitch: a tile's image could periodically paint blank mid-swipe,
 * consistent with a known WebKit behaviour where an off-screen,
 * non-promoted compositor layer gets discarded and has to be
 * re-rasterised the moment it's brought back on screen. Back to native
 * scroll-snap, which hands dragging and momentum to the browser's own
 * compositor instead of a JS-driven transform -- combined with a real
 * multi-copy loop (below), loading="eager" on the active color's slides,
 * and a promoted layer on the track and each slide (aleo-custom-
 * sections.css) so Safari has no reason to discard them.
 *
 * The loop: .aleo-tile__mobile-track holds several full copies of the
 * product's images in a row (not clones of just the end images). The copy
 * count isn't always 3 -- a short track (few images) gets more copies on
 * each side of the middle (at least 3 full copies' worth of slides each
 * side, data-copies-each-side, set in Liquid), since a short track only
 * gave the loop-reset a few slides of runway, and a few fast consecutive
 * swipes could reach the physical end of the scrollable area before the
 * silent reset had a chance to run -- felt like the track "resisted" and
 * needed a second swipe. JS parks the initial scroll position at the
 * start of the middle copy, and re-parks there again at the start of
 * every touch if it's drifted into an outer copy since the last settle
 * (recentreIfDrifted) -- a second safety net independent of the debounce
 * timing below. Once a scroll gesture has fully settled -- via the
 * 'scrollend' event where supported, otherwise a short debounce after the
 * last 'scroll' event -- and landed in the very first or very last copy,
 * it jumps scrollLeft by a whole number of copies back to the equivalent
 * position in the middle copy. Because every copy is pixel-identical, the
 * jump is invisible. scrollLeft is never touched while a finger is down
 * or while momentum is still running -- only after a real settle, or at
 * the safe moment right as a new touch begins.
 *
 * The progress bar's position used to be driven by a CSS custom property
 * (--scroll-wrapped) written every frame and read back via var()/calc().
 * On a real iPhone that line never moved at all -- not reproducible in
 * desktop WebKit -- consistent with a known class of WebKit bugs where a
 * custom-property write on one element doesn't reliably invalidate a
 * descendant's (and especially a pseudo-element's) computed style that
 * depends on it via var()/calc(), so the dependent transform never gets
 * recomputed even though the property itself did change. Rebuilt with
 * none of that: three real sibling elements (main + two "ghost" copies,
 * aleo-custom-sections.css) per track, each positioned by plain
 * element.style.transform writes computed as plain JS numbers from that
 * track's real scrollLeft -- no custom property, no calc(), nothing for a
 * browser to fail to re-resolve. Applied from a requestAnimationFrame
 * loop that runs only from touchstart until the scroll has fully settled
 * (not a single perpetual loop), plus a plain 'scroll' event listener as
 * a backup in case scrollLeft reads stale inside rAF during native
 * touch-driven scrolling on iOS.
 *
 * A fixed_color_value tile (snippets/aleo-product-tile.liquid) pre-
 * renders one full instance of all of the above PER COLOR of the
 * product, stacked on top of each other and toggled with a visibility
 * class (never display:none -- a hidden track must stay measurable for
 * its own State to park it correctly before it's ever shown). Each
 * track/progress pair is matched by a shared data-color-index, since a
 * tile can have more than one progress block and the old tile-wide
 * `tile.querySelector('.aleo-tile__progress')` lookup would silently grab
 * the wrong one. Tapping a swatch calls switchToColor, which shows that
 * color's own track/progress/price and moves the selected outline -- no
 * navigation, no rebuilding of slide content, nothing touched about how
 * any individual track loops or scrolls.
 *
 * Scoped to .aleo-grid .aleo-tile AND .aleo-pdp__gallery (TRACK_SELECTOR/
 * SCOPE_SELECTOR below) -- the sitewide product-block elsewhere
 * (recommendations, search, etc.) is untouched; the desktop hover-swap
 * element set stays exactly as it was, now simply hidden on mobile in
 * favour of this separate track. sections/product-page.liquid's mobile
 * gallery reuses this exact same track/progress markup and this exact
 * same file (switchToColor exposed as window.aleoCarousel.switchToColor,
 * at the bottom) rather than a copy of either -- the loop/scroll/snap/
 * progress-bar mechanics need no changes at all to work there, since they
 * already only ever operate on a track+progress pair via data attributes,
 * never anything grid-specific.
 */
(function () {
  var MOBILE_QUERY = '(max-width: 767.98px)';
  var SETTLE_DEBOUNCE_MS = 120;
  var SUPPORTS_SCROLLEND = 'onscrollend' in window;

  // Grid tiles and the PDP's own mobile gallery (sections/product-page.
  // liquid) both use this exact track/progress markup now -- the PDP
  // reuses this file rather than a copy of it (see this file's header
  // comment). ".closest('.aleo-tile, .aleo-pdp__gallery')" below is the
  // only generalization that needed: both are valid "scope" roots a
  // track can be matched back to its own progress/swatches within.
  var TRACK_SELECTOR = '.aleo-grid .aleo-tile .aleo-tile__mobile-track, .aleo-pdp__gallery .aleo-tile__mobile-track';
  var SCOPE_SELECTOR = '.aleo-tile, .aleo-pdp__gallery';

  function getTracks() {
    return Array.prototype.slice.call(document.querySelectorAll(TRACK_SELECTOR));
  }

  // The progress block belonging to THIS track specifically -- matched by
  // data-color-index when the track carries one (a fixed_color_value
  // tile, or a PDP gallery with more than one color, one progress block
  // per color), otherwise the scope's one and only .aleo-tile__progress
  // (the legacy single-track tile, and a PDP gallery with no Color
  // option at all).
  function getOwnProgress(track) {
    var scope = track.closest(SCOPE_SELECTOR);
    if (!scope) return null;
    var idx = track.dataset.colorIndex;
    if (idx !== undefined) {
      return scope.querySelector('.aleo-tile__progress[data-color-index="' + idx + '"]');
    }
    return scope.querySelector('.aleo-tile__progress');
  }

  function getRealCount(progress) {
    if (!progress) return 0;
    return parseInt(progress.dataset.count, 10) || 0;
  }

  function State(track) {
    this.track = track;
    this.tile = track.closest(SCOPE_SELECTOR);
    this.progress = getOwnProgress(track);
    this.realCount = getRealCount(this.progress);
    this.copiesEachSide = parseInt(track.dataset.copiesEachSide, 10) || 1;
    this.settleTimer = null;
    this.rafId = null;
    this.rafActive = false;
    this.containerWidth = 0;

    this.bars = this.progress
      ? {
          main: this.progress.querySelector('[data-bar-role="main"]'),
          prev: this.progress.querySelector('[data-bar-role="prev"]'),
          next: this.progress.querySelector('[data-bar-role="next"]'),
        }
      : null;
  }

  State.prototype.slideWidth = function () {
    return this.track.clientWidth;
  };

  State.prototype.copyWidth = function () {
    return this.slideWidth() * this.realCount;
  };

  State.prototype.totalCopies = function () {
    return this.copiesEachSide * 2 + 1;
  };

  // The scrollLeft a freshly-parked or freshly-recentred track should sit
  // at: the start of the middle copy.
  State.prototype.parkedScrollLeft = function () {
    return this.copiesEachSide * this.copyWidth();
  };

  // Bar width is a plain pixel number set once (and on resize), not a CSS
  // percentage of --count -- kept consistent with everything else here
  // being plain JS rather than something for a browser to resolve itself.
  State.prototype.layoutBars = function () {
    if (!this.bars || this.realCount < 1 || !this.progress) return;
    var containerWidth = this.progress.clientWidth;
    this.containerWidth = containerWidth;
    var barWidth = containerWidth / this.realCount;
    [this.bars.main, this.bars.prev, this.bars.next].forEach(function (bar) {
      if (bar) bar.style.width = barWidth + 'px';
    });
  };

  // Reads scrollLeft fresh every call -- never cached -- and writes a
  // plain pixel transform straight to each of the three real bar
  // elements. No var(), no calc(): this function is the entire position
  // pipeline.
  State.prototype.updateBarPosition = function () {
    if (!this.bars || this.realCount < 1 || !this.progress) return;
    var slideWidth = this.slideWidth();
    if (!slideWidth) return;
    var containerWidth = this.containerWidth || this.progress.clientWidth;
    if (!containerWidth) return;
    var rawIndex = this.track.scrollLeft / slideWidth - this.copiesEachSide * this.realCount;
    var wrapped = ((rawIndex % this.realCount) + this.realCount) % this.realCount;
    var offset = (wrapped / this.realCount) * containerWidth;
    if (this.bars.main) this.bars.main.style.transform = 'translateX(' + offset + 'px)';
    if (this.bars.prev) this.bars.prev.style.transform = 'translateX(' + (offset - containerWidth) + 'px)';
    if (this.bars.next) this.bars.next.style.transform = 'translateX(' + (offset + containerWidth) + 'px)';
  };

  State.prototype.startRaf = function () {
    if (this.rafActive) return;
    this.rafActive = true;
    var self = this;
    function step() {
      if (!self.rafActive) return;
      self.updateBarPosition();
      self.rafId = requestAnimationFrame(step);
    }
    this.rafId = requestAnimationFrame(step);
  };

  State.prototype.stopRaf = function () {
    this.rafActive = false;
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = null;
  };

  // Only ever called once a scroll has genuinely settled (scrollend, or
  // the debounce fallback below) -- never while a finger is down or
  // momentum is still running. Native scroll-snap always lands scrollLeft
  // on an exact slide-width multiple, and copyWidth is itself an exact
  // multiple of slideWidth, so these boundary comparisons have no
  // sub-pixel ambiguity the way the previous JS-transform version did.
  //
  // Triggers only once the ABSOLUTE first or last copy is reached (not
  // just leaving the original middle one) -- with copiesEachSide copies
  // of buffer on each side, that's deliberately a wide margin, not a
  // single-copy window, so normal swiping essentially never reaches it.
  // When it does trigger, it jumps back by a full copiesEachSide copies
  // in one go, landing exactly back at the equivalent position in the
  // original middle copy and restoring the full buffer on both sides.
  State.prototype.normalise = function () {
    var cw = this.copyWidth();
    if (!cw) return;
    var sl = this.track.scrollLeft;
    var jump = this.copiesEachSide * cw;
    var total = this.totalCopies();
    if (sl < cw) {
      this.track.scrollLeft = sl + jump;
    } else if (sl >= cw * (total - 1)) {
      this.track.scrollLeft = sl - jump;
    }
  };

  // Stricter than normalise() on purpose: called only at the very start
  // of a touch (a safe moment to jump invisibly, before any dragging has
  // happened), this snaps back to the exact equivalent position in the
  // original middle copy whenever the track isn't already sitting there
  // -- not just when it's reached an outer edge. A second, independent
  // safety net against drift, on top of normalise()'s own edge-triggered
  // one.
  State.prototype.recentreIfDrifted = function () {
    var cw = this.copyWidth();
    if (!cw) return;
    var parked = this.parkedScrollLeft();
    var sl = this.track.scrollLeft;
    if (sl >= parked && sl < parked + cw) return;
    var offsetWithinCopy = ((sl % cw) + cw) % cw;
    this.track.scrollLeft = parked + offsetWithinCopy;
  };

  function initTrack(track) {
    if (track._aleoState) return;
    var state = new State(track);
    track._aleoState = state;
    if (state.realCount < 1) return;

    // Park in the middle copy before the user can see anything else --
    // instant, native scrollLeft assignment, no animation. Runs for every
    // track regardless of whether it's the initially-visible one, so an
    // inactive color's track is already correctly parked the moment it's
    // first shown (assets/aleo-grid-mobile-carousel.js's switchToColor
    // re-asserts this too, defensively, right before revealing it).
    track.scrollLeft = state.parkedScrollLeft();
    state.layoutBars();
    state.updateBarPosition();

    function onSettled() {
      state.normalise();
      state.updateBarPosition();
      state.stopRaf();
    }

    if (SUPPORTS_SCROLLEND) {
      track.addEventListener('scrollend', onSettled, { passive: true });
    } else {
      track.addEventListener(
        'scroll',
        function () {
          window.clearTimeout(state.settleTimer);
          state.settleTimer = window.setTimeout(onSettled, SETTLE_DEBOUNCE_MS);
        },
        { passive: true }
      );
    }

    // Backup path: a plain 'scroll' listener updates the bar directly,
    // independent of the rAF loop below, in case scrollLeft reads stale
    // inside requestAnimationFrame during native touch-driven scrolling
    // on some iOS versions (a separate, real possibility from the custom-
    // property bug this whole rewrite was done to get away from).
    track.addEventListener(
      'scroll',
      function () {
        state.updateBarPosition();
      },
      { passive: true }
    );

    // The rAF loop runs only from touchstart until the gesture settles
    // (not a single perpetual loop) -- started here, stopped in
    // onSettled above. A plain tap (touchstart with no actual scroll)
    // would never fire scrollend or the scroll-debounce, so touchend
    // schedules its own check: if scrollLeft hasn't moved since
    // touchstart, nothing is going to settle, so stop here instead of
    // leaving the loop running forever.
    var touchStartScrollLeft = null;
    track.addEventListener(
      'touchstart',
      function () {
        state.recentreIfDrifted();
        touchStartScrollLeft = track.scrollLeft;
        state.startRaf();
      },
      { passive: true }
    );
    track.addEventListener(
      'touchend',
      function () {
        window.setTimeout(function () {
          if (touchStartScrollLeft !== null && track.scrollLeft === touchStartScrollLeft) {
            state.stopRaf();
          }
        }, SETTLE_DEBOUNCE_MS);
      },
      { passive: true }
    );
  }

  function initAll() {
    getTracks().forEach(initTrack);
  }

  var resizeTimer = null;
  window.addEventListener(
    'resize',
    function () {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        getTracks().forEach(function (track) {
          var state = track._aleoState;
          if (!state || state.realCount < 1) return;
          state.layoutBars();
          state.updateBarPosition();
        });
      }, 150);
    },
    { passive: true }
  );

  // decode() every carousel image once the page has loaded -- belt-and-
  // braces alongside loading="eager" + decoding="async" already on the
  // active color's <img> tags (snippets/aleo-product-tile.liquid).
  // Deliberately excludes inactive (--inactive) tracks: their images are
  // loading="lazy" on purpose, and calling decode() on one forces it to
  // start loading regardless of that attribute -- exactly what the lazy-
  // until-tapped-or-near-viewport behaviour below is meant to avoid.
  function decodeAll() {
    document
      .querySelectorAll(
        TRACK_SELECTOR.split(', ').map(function (s) { return s + ':not(.aleo-tile__mobile-track--inactive) .aleo-tile__mobile-slide img'; }).join(', ')
      )
      .forEach(function (img) {
        if (typeof img.decode === 'function') {
          img.decode().catch(function () {});
        }
      });
  }

  // Upgrades one (inactive, lazy-loaded) track's images to eager + decoded
  // -- called right before a swap reveals it, and separately by the
  // IntersectionObserver below once a tile nears the viewport, so images
  // are already warm by the time a swatch might plausibly be tapped.
  // Idempotent: a track is only ever upgraded once.
  function upgradeTrackImages(track) {
    if (track._aleoUpgraded) return;
    track._aleoUpgraded = true;
    track.querySelectorAll('.aleo-tile__mobile-slide img').forEach(function (img) {
      img.loading = 'eager';
      if (typeof img.decode === 'function') {
        img.decode().catch(function () {});
      }
    });
  }

  // Pre-warms every inactive color track's images once its tile is
  // within rootMargin of the viewport, so a tap feels instant rather than
  // waiting on a fresh image load. Only observes tiles that actually have
  // more than one color (fixed_color_value tiles); a legacy single-track
  // tile has nothing inactive to warm.
  if ('IntersectionObserver' in window) {
    var warmObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target
            .querySelectorAll('.aleo-tile__mobile-track--inactive')
            .forEach(upgradeTrackImages);
          warmObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '600px 0px' }
    );
  }

  function observeTileForWarming(tile) {
    if (!warmObserver || tile._aleoWarmObserved) return;
    if (!tile.querySelector('.aleo-tile__mobile-track--inactive')) return;
    tile._aleoWarmObserved = true;
    warmObserver.observe(tile);
  }

  function observeAllForWarming() {
    if (!warmObserver) return;
    document.querySelectorAll(SCOPE_SELECTOR).forEach(observeTileForWarming);
  }

  // Swaps which color's track/progress/price/swatch-outline is showing,
  // in place -- no navigation. Mobile only (desktop still has just the
  // one hover-swap image set per tile, matching its own color; the
  // swatch click handler below navigates there instead). Re-parks the
  // target track defensively right before revealing it: it should
  // already be sitting correctly parked from initTrack() having run for
  // every track at load regardless of visibility, but asserting it again
  // here guarantees a clean first-image start every time.
  function switchToColor(tile, targetIndex) {
    var indexStr = String(targetIndex);
    var targetTrack = tile.querySelector('.aleo-tile__mobile-track[data-color-index="' + indexStr + '"]');
    if (!targetTrack) return;
    var state = targetTrack._aleoState;
    if (!state) return;

    upgradeTrackImages(targetTrack);

    targetTrack.scrollLeft = state.parkedScrollLeft();
    state.layoutBars();
    state.updateBarPosition();

    tile.querySelectorAll('.aleo-tile__mobile-track[data-color-index]').forEach(function (t) {
      t.classList.toggle('aleo-tile__mobile-track--inactive', t !== targetTrack);
    });
    tile.querySelectorAll('.aleo-tile__progress[data-color-index]').forEach(function (p) {
      p.classList.toggle('aleo-tile__progress--inactive', p.dataset.colorIndex !== indexStr);
    });
    tile.querySelectorAll('.aleo-tile__price[data-color-index]').forEach(function (p) {
      p.hidden = p.dataset.colorIndex !== indexStr;
    });
    tile.querySelectorAll('.aleo-tile__swatch[data-color-index]').forEach(function (s) {
      s.classList.toggle('aleo-tile__swatch--selected', s.dataset.colorIndex === indexStr);
      if (s.dataset.colorIndex === indexStr) {
        s.setAttribute('aria-current', 'true');
      } else {
        s.removeAttribute('aria-current');
      }
    });

    var variantId = targetTrack.dataset.variantId;
    var productUrl = tile.dataset.productUrl;
    if (variantId && productUrl) {
      var newHref = productUrl + '?variant=' + variantId;
      tile.querySelectorAll('.aleo-tile__link, .aleo-tile__meta-link').forEach(function (a) {
        a.href = newHref;
      });
    }
  }

  // Desktop counterpart to switchToColor above: swaps which color's
  // .aleo-tile__color-stack is "live" (carries the real .product-block__
  // image base class the stock <product-block> element's own hover-swap/
  // arrow-cycling JS (assets/main.js, ProductBlock) keys off of) rather
  // than scrolling a track. Re-pointing that element's own cached
  // `images` array and calling its own setActiveImage(0) is what hands
  // control straight back to that completely unmodified stock JS --
  // opacity-crossfade hover and modulo-wrapped arrow cycling (already
  // loops continuously) then run exactly as they always have against a
  // single-color tile, just now against whichever color is current. Safe
  // to call repeatedly/redundantly (e.g. from a mouseover handler) since
  // every step is idempotent.
  function switchToColorDesktop(tile, targetIndex) {
    var indexStr = String(targetIndex);
    var targetStack = tile.querySelector('.aleo-tile__color-stack[data-color-index="' + indexStr + '"]');
    if (!targetStack) return;

    tile.querySelectorAll('.aleo-tile__color-stack[data-color-index]').forEach(function (stack) {
      var isTarget = stack === targetStack;
      stack.classList.toggle('aleo-tile__color-stack--inactive', !isTarget);
      stack.querySelectorAll('.aleo-tile__color-image').forEach(function (img) {
        img.classList.toggle('product-block__image', isTarget);
      });
    });

    if (typeof tile.setActiveImage === 'function') {
      tile.images = Array.prototype.slice.call(targetStack.querySelectorAll('.product-block__image'));
      tile.setActiveImage(0);
    }

    tile.querySelectorAll('.aleo-tile__progress[data-color-index]').forEach(function (p) {
      p.classList.toggle('aleo-tile__progress--inactive', p.dataset.colorIndex !== indexStr);
    });
    tile.querySelectorAll('.aleo-tile__price[data-color-index]').forEach(function (p) {
      p.hidden = p.dataset.colorIndex !== indexStr;
    });
    tile.querySelectorAll('.aleo-tile__swatch[data-color-index]').forEach(function (s) {
      s.classList.toggle('aleo-tile__swatch--selected', s.dataset.colorIndex === indexStr);
      if (s.dataset.colorIndex === indexStr) {
        s.setAttribute('aria-current', 'true');
      } else {
        s.removeAttribute('aria-current');
      }
    });

    var targetLink = targetStack.querySelector('.aleo-tile__link');
    var newHref = targetLink ? targetLink.getAttribute('href') : null;
    if (newHref) {
      tile.querySelectorAll('.aleo-tile__meta-link').forEach(function (a) {
        a.href = newHref;
      });
    }
  }

  // One delegated click listener covers every swatch: a fixed_color_value
  // tile's swatches (data-color-index, a plain <button>, no data-media)
  // swap in place either way -- switchToColor's mobile track on mobile,
  // switchToColorDesktop's hover-swap stack on desktop. A legacy tile's
  // swatches (data-media, no data-color-index) are left entirely to the
  // two listeners further down, unchanged.
  document.addEventListener('click', function (evt) {
    var swatch = evt.target.closest('.aleo-grid .aleo-tile .aleo-tile__swatch[data-color-index]');
    if (!swatch) return;
    var tile = swatch.closest('.aleo-tile');
    if (!tile) return;
    var targetIndex = swatch.dataset.colorIndex;

    if (window.matchMedia(MOBILE_QUERY).matches) {
      switchToColor(tile, targetIndex);
    } else {
      switchToColorDesktop(tile, targetIndex);
    }
  });

  // Hovering a swatch previews that color too, not just clicking it --
  // desktop only (mobile has no hover concept; MOBILE_QUERY's own tiles
  // never show these swatches hovered in the first place). `mouseover`,
  // not `mouseenter`, since only the former bubbles and this is a single
  // delegated listener rather than one per swatch; switchToColorDesktop
  // is idempotent, so the repeat calls while the pointer moves across a
  // swatch's own children are harmless.
  document.addEventListener('mouseover', function (evt) {
    if (window.matchMedia(MOBILE_QUERY).matches) return;
    var swatch = evt.target.closest('.aleo-grid .aleo-tile .aleo-tile__swatch[data-color-index]');
    if (!swatch) return;
    var tile = swatch.closest('.aleo-tile');
    if (!tile) return;
    switchToColorDesktop(tile, swatch.dataset.colorIndex);
  });

  // Swatch click -> scroll the matching image (within the middle copy)
  // into view, mobile only. Legacy path only: these swatches carry
  // data-media (the tile-wide default/single-track tile, e.g. the
  // Selections page) -- a fixed_color_value tile's swatches never carry
  // data-media, so this never matches them; the listener above handles
  // those instead. A separate listener from main.js's own swatch handler
  // (ProductBlock.onSelectSwatch), not a replacement for it -- that one
  // still runs and still updates data-media/url state.
  //
  // Also moves the swatch's own selected-outline class here: the stock
  // ProductBlock.onSelectSwatch (main.js) updates which desktop hover-swap
  // image is active, but never touches which swatch LOOKS selected --
  // that was only ever decided once, server-side, at page load, and never
  // moved again. Scoped to .aleo-grid .aleo-tile so the sitewide product-
  // block elsewhere is unaffected; not moved into main.js since
  // --selected is an Aléo-specific modifier class, not a stock one.
  document.addEventListener('click', function (evt) {
    var anySwatch = evt.target.closest('.aleo-grid .aleo-tile .product-block-options__item');
    if (anySwatch) {
      var swatchGroup = anySwatch.closest('.product-block-options');
      if (swatchGroup) {
        swatchGroup.querySelectorAll('.product-block-options__item').forEach(function (el) {
          el.classList.toggle('aleo-tile__swatch--selected', el === anySwatch);
        });
      }
    }

    if (!window.matchMedia(MOBILE_QUERY).matches) return;
    var swatch = evt.target.closest('.aleo-grid .aleo-tile [data-media].product-block-options__item');
    if (!swatch) return;

    var tile = swatch.closest('.aleo-tile');
    var track = tile && tile.querySelector('.aleo-tile__mobile-track');
    var state = track && track._aleoState;
    if (!state || state.realCount < 1) return;

    var slides = Array.prototype.slice.call(track.querySelectorAll('.aleo-tile__mobile-slide'));
    var targetIndex = -1;
    for (var i = 0; i < slides.length; i += 1) {
      if (slides[i].dataset.mediaId === swatch.dataset.media) {
        targetIndex = i;
        break;
      }
    }
    if (targetIndex < 0) return;

    var withinCopy = targetIndex % state.realCount;
    var slideWidth = state.slideWidth();
    track.scrollTo({
      left: (state.copiesEachSide * state.realCount + withinCopy) * slideWidth,
      behavior: 'smooth',
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
      observeAllForWarming();
    });
  } else {
    initAll();
    observeAllForWarming();
  }

  if (document.readyState === 'complete') {
    decodeAll();
  } else {
    window.addEventListener('load', decodeAll);
  }

  // Collection pages can re-render the grid client-side (filters/sort,
  // infinite paginate-style reflows elsewhere in the theme); re-scan
  // periodically-cheap via a MutationObserver on the grid root rather than
  // assuming a one-time DOMContentLoaded pass is enough.
  var gridRoot = document.querySelector('.aleo-grid__products');
  if (gridRoot && 'MutationObserver' in window) {
    var observer = new MutationObserver(function () {
      initAll();
      decodeAll();
      observeAllForWarming();
    });
    observer.observe(gridRoot, { childList: true });
  }

  // Small public surface so another section can reuse this file instead
  // of copying it -- sections/product-page.liquid's own color-change
  // handler calls switchToColor directly on its gallery (a valid "scope"
  // per SCOPE_SELECTOR above) rather than re-implementing the swap.
  window.aleoCarousel = { switchToColor: switchToColor, switchToColorDesktop: switchToColorDesktop };
})();
